import {
  BOOLEAN_SELECT_LIST_ITEMS,
  CrudMode,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatSchoolYear,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors
} from "@compass/core";
import { DatePicker, Select } from "components";
import { StaffEmployment, StaffEmploymentEditResource, StaffEmploymentViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const StaffEmploymentPageBody = (props: {
  crudMode: CrudMode;
  employment: StaffEmployment;
  setEmployment: (value: StaffEmployment) => void;
  viewResource: StaffEmploymentViewResource;
  editResource: StaffEmploymentEditResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints, xSmallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const yearField = () => {
    const propertyName = pn<StaffEmployment>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.employment.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.employment.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StaffEmployment>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.employment.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.employment.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const startDateField = () => {
    const propertyName = pn<StaffEmployment>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.employment.startDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.startDate}
        onChange={(value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.startDate = value;
            })
          )
        }
      />
    );
  };

  const endDateField = () => {
    const propertyName = pn<StaffEmployment>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.employment.endDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.endDate}
        onChange={(value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.endDate = value;
            })
          )
        }
      />
    );
  };

  const isTeacherField = () => {
    const propertyName = pn<StaffEmployment>("isTeacher");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.employment.isTeacher)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.employment.isTeacher)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.isTeacher = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {schoolField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {startDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {endDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {isTeacherField()}
      </Grid>
    </Grid>
  );
};
